import React, { Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import 'react-loading-skeleton/dist/skeleton.css'
import { useNavigate } from 'react-router-dom'

import { useReactiveVar } from '@apollo/client/react'
import Divider from '@mui/material/Divider'
import * as Sentry from '@sentry/browser'
import { addHours, format, setHours, setMinutes } from 'date-fns'
import { Analytics } from 'shared-components'
import { DateSelectionSkeleton } from 'shared-components'
import { RequestAppointmentButton } from 'shared-components'
import { ScrollHelper } from 'shared-components'
import { Subheader } from 'shared-components'

import { Calendar } from '../../components/Calendar'
import { FirstAvailable } from '../../components/FirstAvailable'
import { TimePicker } from '../../components/TimePicker'

import { alertType, message, reservationDateTime, store } from '../../cache'
import { useAvailability } from '../../hooks/useAvailability'
import { getFirstAvailable } from '../../utils/getFirstAvailable'

import './style.css'

export const DatetimeSelect = () => {
  const { locationId, partnerId } = useReactiveVar(store)
  const [selectedDay, setSelectedDay] = useState(null)
  const [firstAvailableDay, setFirstAvailableDay] = useState(null)
  const [availableTimes, setAvailableTimes] = useState([])
  const navigate = useNavigate()
  const { t } = useTranslation()
  const appointmentTakenMsg = t(
    'Oops! That appointment time is no longer available. Please choose a new date or time.'
  )
  const invalidDateMessage = t(
    'Oops! It looks like there is an issue with your request, please try using a different browser or try again later. If this issue persists please contact support@intelibly.com.'
  )

  const [currentDate, setCurrentDate] = useState(new Date())
  const {
    reservations,
    serviceUnavailable,
    invalidStartDate,
    uncaughtError,
    loading,
    error,
  } = useAvailability(60, currentDate)

  const [didLoad, setDidLoad] = useState(false)

  useEffect(() => {
    if (!loading) {
      setDidLoad(true)
    }
  }, [loading])

  useEffect(() => {
    if (didLoad && reservations.length === 0) {
      navigate('/request-appointment')
    }
  }, [didLoad, reservations, navigate])

  useEffect(() => {
    if (serviceUnavailable) {
      navigate('/request-appointment')
      Sentry.captureMessage(
        'Vendor Service Unavailable: ${data.dolphinReservations.message'
      )
    }
  }, [serviceUnavailable, navigate])

  useEffect(() => {
    if (invalidStartDate) {
      alertType('warning')
      message(invalidDateMessage)
      Sentry.captureMessage(
        'Invalid Start Date: ${data.dolphinReservations.message}'
      )
    }
  }, [invalidStartDate])

  useEffect(() => {
    if (uncaughtError) {
      Sentry.captureMessage('Uncaught error in PMS')
    }
  })

  const setInitialState = () => {
    setSelectedDay(null)
    setAvailableTimes([])
  }

  const handleCalendarDateChange = newDate => {
    setInitialState()
    setCurrentDate(newDate)
  }

  const onDaySelected = (day, availability = { times: [] }) => {
    if (day == null || day == undefined) {
      setInitialState()
    } else {
      setSelectedDay(new Date(day))
      setAvailableTimes(availability.times)
      Analytics.dateSelected({
        locationId,
        partnerId,
        dayOfWeek: format(day, 'EEEE'),
        month: format(day, 'MMMM'),
      })
    }
  }

  const onTimeSelected = time => {
    const [hours, minutes] = time.split(':')
    const datetime = setMinutes(setHours(selectedDay, hours), minutes)

    if (isTimeInThePast(datetime)) {
      alertType('warning')
      message(appointmentTakenMsg)
      navigate('/')
      return
    }

    reservationDateTime(datetime)
    navigate('/book-appointment')
    Analytics.timeSelected({ locationId, partnerId, time })
  }

  const isTimeInThePast = datetime => {
    const now = addHours(new Date(), 1)
    return datetime <= now
  }

  useEffect(() => {
    const firstAvailable = getFirstAvailable(reservations)
    setFirstAvailableDay(firstAvailable)

    if (!firstAvailable) {
      onDaySelected(null)
    }
  }, [reservations])

  return (
    <div className="datetime-select">
      <div className="body">
        <Suspense fallback={<DateSelectionSkeleton />}>
          <div className="date-selection">
            <Subheader text={t('Select a date')} />
            <FirstAvailable
              firstAvailableDay={firstAvailableDay}
              onDaySelected={onDaySelected}
              onChangeDate={handleCalendarDateChange}
            />
            <Calendar
              selectedDay={selectedDay}
              onDaySelected={onDaySelected}
              currentDate={currentDate}
              onChangeDate={handleCalendarDateChange}
              reservations={reservations}
            />
          </div>
          {availableTimes.length > 0 ? (
            <div className="time-selection">
              <Subheader text={t('Select a time')} />
              <TimePicker
                onTimeSelected={onTimeSelected}
                availableTimes={availableTimes}
              />
            </div>
          ) : null}
        </Suspense>
        <Divider />
        <RequestAppointmentButton />
        <ScrollHelper />
      </div>
    </div>
  )
}
